<script lang="ts" setup>
const title: string = 'Cookies Policy';

interface CookieInUseInfo {
  name: string;
  purpose: string;
  duration: string;
  category: string;
}

const cookiesInUse: CookieInUseInfo[] = [
  {
    name: '_ga',
    purpose: 'Used by Google Analytics to track visitor behavior and help us improve the website. The data collected does not directly identify anyone.',
    duration: '2 years',
    category: 'Performance'
  },
  {
    name: '_gid',
    purpose: 'Used to distinguish visitors and collect usage information.',
    duration: '1 day',
    category: 'Performance'
  }
];

useSeoMeta({ title });
</script>

<template>
  <UContainer class="cookies-policy">
    <UCard
      class="my-4"
      :ui="{ header: { base: 'bg-card-contrast rounded-t-lg' } }"
    >
      <template #header>
        <h1 class="text-2xl font-semibold">{{ title }}</h1>
      </template>

      <p class="text-sm leading-7">
        This website uses cookies to analyze how visitors interact with the site and improve your experience.
        Below, we explain the cookies we use and their purpose.
      </p>

      <h2 class="text-2xl py-10">What Are Cookies?</h2>
      <p class="text-sm leading-7">
        Cookies are small text files stored on your device when you visit a website. They help websites remember
        information about your visit, such as your preferences or usage patterns.
      </p>

      <h2 class="text-2xl py-10">Cookies We Use</h2>

      <template v-for="cookies, index in cookiesInUse" :key="cookies.name">
        <hr v-if="index > 0" class="my-4">

        <div><strong>Name:</strong> {{ cookies.name }}</div>
        <div><strong>Purpose:</strong> {{ cookies.purpose }}</div>
        <div><strong>Duration:</strong> {{ cookies.duration }}</div>
        <div><strong>Category:</strong> {{ cookies.category }}</div>
      </template>

      <h2 class="text-2xl py-10">Third-Party Providers</h2>
      <p class="text-sm leading-7">
        We use Google Analytics to understand how visitors use the website. The data is collected without
        identifying anyone directly. Learn more in
        <a
          class="text-blue-500 underline"
          href="https://policies.google.com/technologies/cookies"
        >
          Google’s cookie documentation
        </a>.
      </p>

      <h2 class="text-2xl py-10">Managing Cookies</h2>
      <p class="text-sm leading-7">
        You can disable or delete cookies through your browser settings at any time. Disabling cookies
        may impact some features of this website. Here are links to manage cookies for popular browsers:
      </p>

      <ul class="list-disc ml-8 mb-4">
        <li>
          <a
            class="text-blue-500 underline"
            href="https://support.google.com/chrome/answer/95647"
          >
            Chrome
          </a>
        </li>
        <li>
          <a
            class="text-blue-500 underline"
            href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
          >
            Firefox
          </a>
        </li>
        <li>
          <a
            class="text-blue-500 underline"
            href="https://support.apple.com/en-us/guide/safari/sfri11471/mac"
          >
            Safari
          </a>
        </li>
      </ul>
    </UCard>
  </UContainer>
</template>

<style lang="scss" scoped>
.cookies-policy {
  padding-top: var(--header-height);
}
</style>
